// The mapping editor (milestone 7): a build spec as forms. Inputs, joins,
// the scope filter, output fields (1:1 from a column or a CEL expression),
// grouping, validation rules and keys. Every CEL expression is checked as
// you type against the same scope the build uses (the admin API), with
// highlighting and autocomplete. The JSON view edits the same spec.
import { useQuery } from '@tanstack/react-query'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { api, columnTypes, type ExprCheck, type ExprTarget } from '../api'
import { CelEditor } from '../cel/CelEditor'

export interface SpecInput {
  alias: string
  kind: 'source_table' | 'bucket'
  slug: string
  select?: 'latest' | 'new' | 'all'
  unnest?: { expr: string; as: string }
}
export interface SpecField {
  name: string
  type: string
  from?: string
  expr?: string
  description?: string
}
export interface SpecJoin {
  left: string
  right: string
  type: 'left' | 'inner'
  on: [string, string][]
}
export interface SpecRule {
  name: string
  expr: string
  reason: string
}
export interface BuildSpecDraft {
  bucket: string
  mode?: 'replace' | 'append'
  language?: 'cel' | 'sql'
  inputs: SpecInput[]
  joins?: SpecJoin[]
  where?: string
  fields: SpecField[]
  group_by?: string[]
  rules?: SpecRule[]
  primary_key?: string[]
  indexes?: string[][]
  [k: string]: unknown
}

interface Props {
  slug: string
  spec: BuildSpecDraft
  onChange: (s: BuildSpecDraft) => void
  readOnly: boolean
}

const csv = (v: string[] | undefined) => (v ?? []).join(', ')
const uncsv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean)

export function MappingEditor({ slug, spec, onChange, readOnly }: Props) {
  const fns = useQuery({ queryKey: ['cel-functions'], queryFn: api.celFunctions, staleTime: Infinity })
  const tables = useQuery({ queryKey: ['source-tables'], queryFn: api.sourceTables })
  const buckets = useQuery({ queryKey: ['buckets'], queryFn: api.buckets })
  const cel = spec.language === 'cel'
  // The checker always sees the latest draft.
  const specRef = useRef(spec)
  useLayoutEffect(() => {
    specRef.current = spec
  }, [spec])
  const checker = useCallback((target: ExprTarget) => (src: string) => api.checkExpression(slug, specRef.current, target, src), [slug])
  const set = (patch: Partial<BuildSpecDraft>) => onChange({ ...spec, ...patch })

  // Columns per input alias, for the 1:1 field picker.
  const columns = useMemo(() => {
    const out: Record<string, { name: string; type: string }[]> = {}
    for (const i of spec.inputs) {
      const cols = i.kind === 'bucket' ? buckets.data?.find((b) => b.slug === i.slug)?.columns : tables.data?.find((t) => t.slug === i.slug)?.columns
      out[i.alias] = [...(cols ?? [])]
      if (i.unnest?.as) out[i.alias].push({ name: i.unnest.as, type: 'unnested' })
    }
    return out
  }, [spec.inputs, tables.data, buckets.data])
  const columnOptions = Object.entries(columns).flatMap(([a, cols]) => cols.map((c) => ({ value: `${a}.${c.name}`, label: `${a}.${c.name} · ${c.type}` })))

  const Expr = ({ value, target, onValue, label }: { value: string; target: ExprTarget; onValue: (v: string) => void; label: string }) =>
    cel ? (
      <ExprBox value={value} onValue={onValue} check={checker(target)} functions={fns.data?.functions ?? []} readOnly={readOnly} label={label} />
    ) : (
      <input className="mono grow" value={value} readOnly={readOnly} aria-label={label} onChange={(e) => onValue(e.target.value)} />
    )

  const upd = <T,>(list: T[], i: number, patch: Partial<T>) => list.map((x, j) => (j === i ? { ...x, ...patch } : x))
  const del = <T,>(list: T[], i: number) => list.filter((_, j) => j !== i)

  return (
    <div className="mapping">
      {!cel && (
        <p className="callout warn">
          This spec uses the older SQL-like expressions. New versions should use CEL (checked, highlighted and autocompleted as you type).
          {!readOnly && (
            <button className="btn btn-sm" style={{ marginLeft: 10 }} onClick={() => set({ language: 'cel' })}>
              Switch to CEL
            </button>
          )}
        </p>
      )}

      <section>
        <h3 className="h3">Inputs</h3>
        <table className="tbl">
          <thead><tr><th>Alias</th><th>Kind</th><th>Table or bucket</th><th>Rows</th><th>Unnest (one row per list item)</th><th /></tr></thead>
          <tbody>
            {spec.inputs.map((inp, i) => (
              <tr key={i}>
                <td><input className="mono" size={6} value={inp.alias} readOnly={readOnly} onChange={(e) => set({ inputs: upd(spec.inputs, i, { alias: e.target.value }) })} /></td>
                <td>
                  <select value={inp.kind} disabled={readOnly} onChange={(e) => set({ inputs: upd(spec.inputs, i, { kind: e.target.value as SpecInput['kind'], slug: '' }) })}>
                    <option value="source_table">source table</option><option value="bucket">bucket</option>
                  </select>
                </td>
                <td>
                  <select value={inp.slug} disabled={readOnly} onChange={(e) => set({ inputs: upd(spec.inputs, i, { slug: e.target.value }) })}>
                    <option value="">choose…</option>
                    {(inp.kind === 'bucket' ? (buckets.data ?? []).filter((b) => b.slug !== slug).map((b) => b.slug) : (tables.data ?? []).map((t) => t.slug)).map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select value={inp.select ?? 'latest'} disabled={readOnly || inp.kind === 'bucket'} onChange={(e) => set({ inputs: upd(spec.inputs, i, { select: e.target.value as SpecInput['select'] }) })}>
                    <option value="latest">latest</option><option value="new">new since last build</option><option value="all">all stored</option>
                  </select>
                </td>
                <td className="unnest">
                  {inp.unnest ? (
                    <div className="row">
                      {Expr({ value: inp.unnest.expr, target: { kind: 'unnest', input: inp.alias }, label: `unnest of ${inp.alias}`,
                        onValue: (v) => set({ inputs: upd(spec.inputs, i, { unnest: { ...inp.unnest!, expr: v } }) }) })}
                      <span className="muted">as</span>
                      <input className="mono" size={10} value={inp.unnest.as} readOnly={readOnly} onChange={(e) => set({ inputs: upd(spec.inputs, i, { unnest: { ...inp.unnest!, as: e.target.value } }) })} />
                      {!readOnly && <button className="btn btn-sm" onClick={() => set({ inputs: upd(spec.inputs, i, { unnest: undefined }) })}>×</button>}
                    </div>
                  ) : (
                    !readOnly && <button className="btn btn-sm" onClick={() => set({ inputs: upd(spec.inputs, i, { unnest: { expr: '', as: 'item' } }) })}>Add unnest</button>
                  )}
                </td>
                <td>{!readOnly && spec.inputs.length > 1 && <button className="btn btn-sm" onClick={() => set({ inputs: del(spec.inputs, i) })}>Remove</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!readOnly && (
          <button className="btn btn-sm" onClick={() => set({ inputs: [...spec.inputs, { alias: `in${spec.inputs.length + 1}`, kind: 'source_table', slug: '', select: 'latest' }] })}>
            Add input
          </button>
        )}
      </section>

      {spec.inputs.length > 1 && (
        <section>
          <h3 className="h3">Joins</h3>
          {(spec.joins ?? []).map((j, i) => (
            <div key={i} className="row">
              <select value={j.left} disabled={readOnly} onChange={(e) => set({ joins: upd(spec.joins!, i, { left: e.target.value }) })}>
                {spec.inputs.map((x) => <option key={x.alias}>{x.alias}</option>)}
              </select>
              <select value={j.type} disabled={readOnly} onChange={(e) => set({ joins: upd(spec.joins!, i, { type: e.target.value as SpecJoin['type'] }) })}>
                <option value="left">left join</option><option value="inner">inner join</option>
              </select>
              <select value={j.right} disabled={readOnly} onChange={(e) => set({ joins: upd(spec.joins!, i, { right: e.target.value }) })}>
                {spec.inputs.map((x) => <option key={x.alias}>{x.alias}</option>)}
              </select>
              <span className="muted">on</span>
              <input className="mono grow" placeholder="r.domain = d.domain, ..." readOnly={readOnly}
                value={j.on.map(([a, b]) => `${a} = ${b}`).join(', ')}
                onChange={(e) => set({ joins: upd(spec.joins!, i, { on: e.target.value.split(',').map((p) => p.split('=').map((x) => x.trim()) as [string, string]).filter((p) => p.length === 2 && p[0] && p[1]) }) })} />
              {!readOnly && <button className="btn btn-sm" onClick={() => set({ joins: del(spec.joins!, i) })}>Remove</button>}
            </div>
          ))}
          {!readOnly && (
            <button className="btn btn-sm" onClick={() => set({ joins: [...(spec.joins ?? []), { left: spec.inputs[0].alias, right: spec.inputs[1].alias, type: 'left', on: [] }] })}>
              Add join
            </button>
          )}
        </section>
      )}

      <section>
        <h3 className="h3">Scope filter <span className="muted note">rows outside it are counted, not quarantined</span></h3>
        {Expr({ value: spec.where ?? '', target: { kind: 'where' }, label: 'where', onValue: (v) => set({ where: v || undefined }) })}
      </section>

      <section>
        <h3 className="h3">Fields</h3>
        <table className="tbl fields">
          <thead><tr><th>Name</th><th>Type</th><th>Value</th><th>Description</th><th /></tr></thead>
          <tbody>
            {spec.fields.map((f, i) => (
              <tr key={i}>
                <td><input className="mono" size={14} value={f.name} readOnly={readOnly} onChange={(e) => set({ fields: upd(spec.fields, i, { name: e.target.value }) })} /></td>
                <td>
                  <select value={f.type} disabled={readOnly} onChange={(e) => set({ fields: upd(spec.fields, i, { type: e.target.value }) })}>
                    {columnTypes.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </td>
                <td className="value">
                  <div className="row">
                    <select aria-label="Value kind" value={f.from !== undefined ? 'from' : 'expr'} disabled={readOnly}
                      onChange={(e) => set({ fields: upd(spec.fields, i, e.target.value === 'from' ? { from: f.expr && /^\w+\.\w+$/.test(f.expr) ? f.expr : '', expr: undefined } : { expr: f.from ?? '', from: undefined }) })}>
                      <option value="from">column</option><option value="expr">expression</option>
                    </select>
                    {f.from !== undefined ? (
                      <select className="grow" value={f.from} disabled={readOnly} onChange={(e) => set({ fields: upd(spec.fields, i, { from: e.target.value }) })}>
                        <option value="">choose…</option>
                        {columnOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <div className="grow">
                        {Expr({ value: f.expr ?? '', target: { kind: 'field' }, label: `field ${f.name}`, onValue: (v) => set({ fields: upd(spec.fields, i, { expr: v }) }) })}
                      </div>
                    )}
                  </div>
                </td>
                <td><input value={f.description ?? ''} readOnly={readOnly} onChange={(e) => set({ fields: upd(spec.fields, i, { description: e.target.value || undefined }) })} /></td>
                <td className="row">
                  {!readOnly && (
                    <>
                      <button className="btn btn-sm" disabled={i === 0} aria-label="Move up" onClick={() => { const n = [...spec.fields]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; set({ fields: n }) }}>↑</button>
                      <button className="btn btn-sm" onClick={() => set({ fields: del(spec.fields, i) })}>Remove</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!readOnly && (
          <button className="btn btn-sm" onClick={() => set({ fields: [...spec.fields, { name: `field_${spec.fields.length + 1}`, type: 'VARCHAR', expr: '' }] })}>
            Add field
          </button>
        )}
      </section>

      <section className="form-grid two-col">
        <label>Group by (field names or expressions, comma-separated)
          <input className="mono" value={csv(spec.group_by)} readOnly={readOnly} onChange={(e) => set({ group_by: uncsv(e.target.value) })} />
        </label>
        <label>Mode
          <select value={spec.mode ?? 'replace'} disabled={readOnly} onChange={(e) => set({ mode: e.target.value as BuildSpecDraft['mode'] })}>
            <option value="replace">replace (rebuild from the inputs)</option><option value="append">append (merge by primary key)</option>
          </select>
        </label>
        <label>Primary key (fields)
          <input className="mono" value={csv(spec.primary_key)} readOnly={readOnly} onChange={(e) => set({ primary_key: uncsv(e.target.value) })} />
        </label>
        <label>Indexes (one per line, fields comma-separated)
          <textarea className="mono" rows={2} value={(spec.indexes ?? []).map((ix) => ix.join(', ')).join('\n')} readOnly={readOnly}
            onChange={(e) => set({ indexes: e.target.value.split('\n').map(uncsv).filter((x) => x.length > 0) })} />
        </label>
      </section>

      <section>
        <h3 className="h3">Validation rules <span className="muted note">rows where a rule is not true go to quarantine; rules see the output fields</span></h3>
        {(spec.rules ?? []).map((r, i) => (
          <div key={i} className="rule">
            <input className="mono" size={18} value={r.name} placeholder="name" readOnly={readOnly} onChange={(e) => set({ rules: upd(spec.rules!, i, { name: e.target.value }) })} />
            <div className="grow">{Expr({ value: r.expr, target: { kind: 'rule' }, label: `rule ${r.name}`, onValue: (v) => set({ rules: upd(spec.rules!, i, { expr: v }) }) })}</div>
            <input className="grow" value={r.reason} placeholder="reason shown in quarantine" readOnly={readOnly} onChange={(e) => set({ rules: upd(spec.rules!, i, { reason: e.target.value }) })} />
            {!readOnly && <button className="btn btn-sm" onClick={() => set({ rules: del(spec.rules!, i) })}>Remove</button>}
          </div>
        ))}
        {!readOnly && (
          <button className="btn btn-sm" onClick={() => set({ rules: [...(spec.rules ?? []), { name: `rule_${(spec.rules ?? []).length + 1}`, expr: '', reason: '' }] })}>
            Add rule
          </button>
        )}
      </section>

      {cel && <FunctionHelp />}
    </div>
  )
}

/** A CEL editor with the checked type underneath. */
function ExprBox({ value, onValue, check, functions, readOnly, label }: {
  value: string
  onValue: (v: string) => void
  check: (src: string) => Promise<ExprCheck>
  functions: NonNullable<Parameters<typeof CelEditor>[0]['functions']>
  readOnly: boolean
  label: string
}) {
  const [checked, setChecked] = useState<ExprCheck | null>(null)
  return (
    <div className="exprbox">
      <CelEditor value={value} onChange={onValue} check={check} functions={functions} readOnly={readOnly} onChecked={setChecked} ariaLabel={label} />
      {checked?.ok && (
        <span className="note mono" title={checked.sql}>
          → {checked.type}
          {checked.aggregate ? ' · aggregate' : ''}
        </span>
      )}
    </div>
  )
}

function FunctionHelp() {
  const fns = useQuery({ queryKey: ['cel-functions'], queryFn: api.celFunctions, staleTime: Infinity })
  const groups = useMemo(() => {
    const g = new Map<string, NonNullable<typeof fns.data>['functions']>()
    for (const f of fns.data?.functions ?? []) g.set(f.group, [...(g.get(f.group) ?? []), f])
    return [...g.entries()]
  }, [fns.data])
  return (
    <details className="fnhelp">
      <summary>CEL functions available in builds</summary>
      <p className="note">
        Operators: <code>== != &lt; &lt;= &gt; &gt;= && || ! ? :</code>, <code>+</code> (numbers, text, lists), <code>in</code>, <code>list[0]</code>.
        Columns are <code>alias.column</code> (or just <code>column</code> when only one input has it). <code>x == null</code> tests for missing values.
      </p>
      <div className="fngrid">
        {groups.map(([group, list]) => (
          <div key={group}>
            <h4>{group}</h4>
            {list.map((f) => (
              <p key={f.name}>
                <code>{f.signature}</code> <span className="muted">{f.doc}</span>
                {f.example && <> <code className="ex">{f.example}</code></>}
              </p>
            ))}
          </div>
        ))}
      </div>
    </details>
  )
}
